/**
 * 63. BOMs / Routings — BOM editor, versions, where-used; routing
 * operations and work centers.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatQuantity, formatDate } from "@/lib/money";

interface BomRow {
  id: string;
  name: string;
  productSku: string;
  productName: string;
  version: number;
  quantity: string;
  lineCount: number;
  operationCount: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  status: "Draft" | "Approved" | "Active" | "Superseded";
}

export function BomsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<BomRow>(
    ["manufacturing", "boms"],
    "/manufacturing/boms",
  );

  return (
    <div>
      <PageHeader
        title="BOMs / Routings"
        subtitle="Bill of materials versions, where-used, routing operations and work centers."
        actions={
          <PendingButton variant="primary" reason="manufacturing" slice="MF-W1">
            {t("documentActions.newBom")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`BOMs (${data.total})`}>
          <DataTable<BomRow>
            rows={data.items}
            columns={[
              { key: "name", label: "Name" },
              {
                key: "productSku",
                label: "Product",
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "version", label: "Version", numeric: true },
              {
                key: "quantity",
                label: "Qty",
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              { key: "lineCount", label: "Lines", numeric: true },
              { key: "operationCount", label: "Operations", numeric: true },
              {
                key: "effectiveFrom",
                label: "Effective from",
                render: (r) =>
                  r.effectiveFrom ? formatDate(r.effectiveFrom) : "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} />
                  ) : (
                    <Badge
                      tone={
                        r.status === "Approved"
                          ? "primary"
                          : r.status === "Superseded"
                            ? "default"
                            : "warning"
                      }
                    >
                      {r.status}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
